import Vue from 'vue';

const isEmpty = (data) => Vue.prototype.$isEmpty(data);
const isNotEmpty = (data) => Vue.prototype.$isNotEmptyV2(data);

/**
 * Only DECREASE formulas lower the price, so they are the only ones worth showing.
 * @param {Array} formulaList raw formula list of a promotion
 * @returns {boolean}
 */
export function validatePromotionFormula(formulaList){
  const isListArray = Array.isArray(formulaList);
  if(!isListArray) return false;

  const selectedFormula = formulaList[0];
  const isFormulaValid = selectedFormula.calculate_by === 'DECREASE';
  return isFormulaValid;
}

/**
 * @param {Array} formulaList raw formula list of a promotion
 * @param {Function} formatAmount currency formatter for AMOUNT type values
 * @returns {?string} display value such as "20%" or "IDR 250K", null when unsupported
 */
export function generateDiscountAmount(formulaList, formatAmount){
  const isFormulaValid = validatePromotionFormula(formulaList);
  if(!isFormulaValid) return null;

  const formula = formulaList[0];
  const { value, value_type: valueType } = formula;
  const valueString = valueType === 'PERCENT' ? `${value}%` : formatAmount(value);

  return valueString;
}

/**
 * Picks the formula with the highest value out of a multi-formula promotion.
 * Mixed PERCENT + AMOUNT sets are not comparable, so the first one is returned as-is.
 * @param {Array} formulaList raw formula list of a promotion
 * @returns {?Array|?Object} the winning formula list, or null when none is valid
 */
export function generateBiggestFormula(formulaList){
  const isFormulaSub = formulaList.some((formula) => isNotEmpty(formula.formula_list));
  let validFormulaType = null;

  if(isFormulaSub){
    validFormulaType = formulaList.filter((formula) => validatePromotionFormula(formula.formula_list));
    if(isNotEmpty(validFormulaType))
      validFormulaType = validFormulaType.map((formula) => formula.formula_list[0]);
  }else{
    validFormulaType = validatePromotionFormula(formulaList) ? formulaList : null;
  }
  if(isEmpty(validFormulaType)) return null;

  const validateFormulaList = (valueType) => validFormulaType.every((formula) => formula.value_type === valueType);

  const isAllPercent = validateFormulaList('PERCENT');
  const isAllAmount = validateFormulaList('AMOUNT');
  const isCombine = !(isAllPercent || isAllAmount);

  if(isCombine) return validFormulaType[0];

  const sortedList = validFormulaType.sort((a, b) => b.value - a.value);
  return [sortedList[0]];
}

/**
 * Keeps only promotions whose discount can be rendered, tagging each with `formula_text`.
 * @param {Array} promotionList promotions from the search-room/promotion endpoint
 * @param {Function} formatAmount currency formatter for AMOUNT type values
 * @returns {Array} displayable promotions
 */
export function parseDiscountAmount(promotionList, formatAmount){
  const duplicatePromotions = Vue.prototype.$deepCopy(promotionList);
  const updatedPromotions = [];

  const setUpdatedPromotions = (formulaList, originalData) => {
    const discountAmount = generateDiscountAmount(formulaList, formatAmount);
    if(isNotEmpty(discountAmount)){
      const updatedPromotion = Object.assign(originalData, { formula_text: discountAmount });
      updatedPromotions.push(updatedPromotion);
    }
  };

  duplicatePromotions.forEach((promotion) => {
    const { formula } = promotion;

    const isMultipleFormula = Array.isArray(formula);
    if(isMultipleFormula){
      const biggestFormula = generateBiggestFormula(formula);
      if(biggestFormula !== null) setUpdatedPromotions(biggestFormula, promotion);
    }else{
      setUpdatedPromotions(formula.formula_list, promotion);
    }
  });

  return updatedPromotions;
}

/**
 * Attaches the human readable booking conditions of each promotion as `rule_list`.
 * @param {Array} promotionList displayable promotions
 * @returns {Array} promotions with `rule_list`
 */
export function generatePromotionRule(promotionList){
  return promotionList.map((promotion) => {
    const { min_night, promotion_type, cut_off, formula } = promotion;

    promotion.rule_list = [];

    if(min_night > 1){
      promotion.rule_list.push(`Minimum ${min_night}-night stay required.`);
    }

    const isMultiNightWithCutOff = promotion_type === 'MULTI_NIGHT' && cut_off > 0;
    const isEarlyBirdWithCutOff = promotion_type === 'EARLY_BIRD' && formula.cut_off > 0;
    if(isMultiNightWithCutOff || isEarlyBirdWithCutOff){
      const cutOff = promotion_type === 'MULTI_NIGHT' ? cut_off : formula.cut_off;
      promotion.rule_list.push(`Book ${cutOff}+ days before check-in.`);
    }
    return promotion;
  });
}



// WEBPACK FOOTER //
// ./src/components/hotel/be/v3/promotion/promotionFormula.js